import type { AxiosRequestConfig, Method } from 'axios';

import { isRPCErrorResponse, RPCError } from 'src/apis/utils';
import { client, createApiCall, rpcClient } from 'src/utils/api';

interface RPCServiceDeclaration<TReq> {
  method: string;
  data?: TReq;
}

interface RESTServiceDeclaration<TReq> {
  method: Method;
  path: string;
  data?: TReq;
}

export type ServiceDeclaration<TReq> = RPCServiceDeclaration<TReq> | RESTServiceDeclaration<TReq>;

export interface APIClientType {
  call<TRes, TReq>(service: ServiceDeclaration<TReq>): Promise<TRes>;
}

export class JSONRPCClient implements APIClientType {
  public async call<TRes, TReq>({ method, data }: RPCServiceDeclaration<TReq>): Promise<TRes> {
    const response = await rpcClient<TReq, TRes>(method)(data);

    if (isRPCErrorResponse(response.data)) {
      throw new RPCError(response.data);
    }

    return response.data.result;
  }
}

export class RESTClient implements APIClientType {
  public async call<TRes, TReq>({
    method,
    path,
    data,
  }: RESTServiceDeclaration<TReq>): Promise<TRes> {
    const response = await createApiCall<TReq, TRes>(method, path)(data);

    return response.data.result;
  }
}

export class RawRESTClient implements APIClientType {
  public async call<TRes, TReq>({
    method,
    path,
    data,
  }: RESTServiceDeclaration<TReq>): Promise<TRes> {
    const config: AxiosRequestConfig = {
      method,
      url: path,
    };

    // GET 요청인 경우 params(=URL), POST 요청인 경우 body에 파라미터 추가
    if (method.toLowerCase() === 'get') {
      config.params = data;
    } else {
      config.data = data;
    }

    const response = await client(config);

    return response.data as TRes;
  }
}
